import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useConfigId } from '../../contexts/ConfigContext'
import { useToast } from '../../contexts/ToastContext'
import {
  getProactiveFollowupRules,
  updateProactiveFollowupRules,
  getVariableDefinitions,
} from '../../api/graph_settings'
import { getApiErrorMessage } from '../../api'
import ConditionBuilder, { ALL_CONDITION_PARAM_KEYS } from '../../components/ConditionBuilder'
import { PageSection, SettingsFormWithPanel } from '../../components/ui'
import { SettingsItemCard } from '../../components/settings'
import ConfirmModal from '../../components/ConfirmModal'
import '../../styles/pages/page-common.css'

const DEFAULT_INTERVAL_MINUTES = 24 * 60
const INTERVAL_STEP_MINUTES = 10
const MAX_INTERVAL_MINUTES = 30 * 24 * 60

function readRuleMinutes(rule) {
  if (rule?.minutes != null) return Number(rule.minutes)
  if (rule?.interval_minutes != null) return Number(rule.interval_minutes)
  if (rule?.hours != null) return Number(rule.hours) * 60
  return DEFAULT_INTERVAL_MINUTES
}

function normalizeIntervalMinutes(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return DEFAULT_INTERVAL_MINUTES
  const rounded = Math.round(n / INTERVAL_STEP_MINUTES) * INTERVAL_STEP_MINUTES
  return Math.max(INTERVAL_STEP_MINUTES, Math.min(MAX_INTERVAL_MINUTES, rounded))
}

function emptyRule() {
  return {
    label: '',
    conditions: { param: ALL_CONDITION_PARAM_KEYS[0] || 'engagement_score', op: 'gte', value: 0 },
    minutes: DEFAULT_INTERVAL_MINUTES,
    prompt: '',
  }
}

function normalizeRule(rule) {
  return {
    ...emptyRule(),
    ...rule,
    minutes: normalizeIntervalMinutes(readRuleMinutes(rule)),
    conditions: rule?.conditions || emptyRule().conditions,
  }
}

function toRulePayload(rule) {
  const payload = {
    conditions: rule?.conditions || emptyRule().conditions,
    minutes: normalizeIntervalMinutes(readRuleMinutes(rule)),
  }
  const label = String(rule?.label || '').trim()
  const trigger = String(rule?.trigger || '').trim()
  const prompt = String(rule?.prompt || rule?.prompt_text || '').trim()
  if (label) payload.label = label
  if (trigger) payload.trigger = trigger
  if (prompt) payload.prompt = prompt
  return payload
}

export default function SettingsProactive() {
  const { t } = useI18n()
  const { showToast } = useToast()
  const configId = useConfigId()
  const [rules, setRules] = useState([])
  const [initialRules, setInitialRules] = useState([])
  const [l4Definitions, setL4Definitions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [error, setError] = useState(null)
  const [deleteRuleIdx, setDeleteRuleIdx] = useState(null)

  useEffect(() => {
    Promise.all([
      getProactiveFollowupRules(configId),
      getVariableDefinitions(configId),
    ])
      .then(([r, vd]) => {
        const raw = Array.isArray(r?.rules) ? r.rules : []
        const normalized = raw.map(normalizeRule)
        const list = normalized
        setRules(list)
        setInitialRules(list)
        const defs = vd?.definitions || []
        setL4Definitions(Array.isArray(defs) ? defs : [])
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [configId])

  const l4Keys = l4Definitions?.map((d) => d.key).filter(Boolean) || []
  const paramKeys = [...new Set([...ALL_CONDITION_PARAM_KEYS, ...l4Keys])]

  async function handleSaveRules() {
    setSaving('rules')
    setError(null)
    try {
      const payload = rules.map(toRulePayload)
      await updateProactiveFollowupRules(payload, configId)
      setRules(payload)
      setInitialRules(payload)
      showToast(t('toast.settings.saved'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.settings.saveFailed'))
      showToast(msg, 'error')
      setError(msg)
    } finally {
      setSaving(null)
    }
  }

  function updateRule(idx, updates) {
    setRules((prev) => {
      const next = [...prev]
      next[idx] = { ...(next[idx] || emptyRule()), ...updates }
      return next
    })
  }

  function addRule() {
    setRules((prev) => [...prev, emptyRule()])
  }

  function removeRule(idx) {
    setRules((prev) => prev.filter((_, i) => i !== idx))
    setDeleteRuleIdx(null)
  }

  const isDirty = JSON.stringify(rules) !== JSON.stringify(initialRules)

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>
  if (error) return <p className="settings-error">{error}</p>

  return (
    <SettingsFormWithPanel dirty={isDirty} saving={saving === 'rules'} onSave={handleSaveRules}>
      <PageSection
        title={t('settings.nav.proactive')}
        subtitle={
          <>
            <p className="settings-hint">{t('settings.proactive.intro')}</p>
            <p className="settings-hint">
              <Link to="../agents/proactive_message_generator" className="settings-inline-link">
                {t('settings.proactive.agentConfigLink')}
              </Link>
            </p>
          </>
        }
        contentClassName="settings-section-block"
      >
        <div className="proactive-rules-block">
          <div className="proactive-logic-title">{t('settings.proactive.rulesTitle')}</div>
          <p className="proactive-logic-intro">{t('settings.proactive.rulesIntro')}</p>
          {rules.length === 0 && (
            <p className="settings-hint proactive-rules-empty">{t('settings.proactive.noRules')}</p>
          )}
          {rules.map((rule, idx) => (
            <SettingsItemCard
              key={idx}
              label={t('settings.proactive.ruleLabel', { n: idx + 1 })}
              onDelete={() => setDeleteRuleIdx(idx)}
              deleteTitle={t('common.delete')}
              className="proactive-rule-card"
            >
              <div className="proactive-rule-meta">
                <label className="proactive-rule-field">
                  <span>{t('settings.proactive.ruleNameLabel')}</span>
                  <input
                    type="text"
                    value={rule.label || ''}
                    placeholder={t('settings.proactive.ruleNamePlaceholder')}
                    onChange={(e) => updateRule(idx, { label: e.target.value })}
                  />
                </label>
                <label className="proactive-rule-field proactive-rule-field--prompt">
                  <span>{t('settings.proactive.rulePromptLabel')}</span>
                  <textarea
                    value={rule.prompt || rule.prompt_text || ''}
                    placeholder={t('settings.proactive.rulePromptPlaceholder')}
                    rows={4}
                    onChange={(e) => updateRule(idx, { prompt: e.target.value })}
                  />
                </label>
              </div>
              <ConditionBuilder
                  condition={rule.conditions}
                  onChange={(cond) => updateRule(idx, { conditions: cond })}
                  paramKeys={paramKeys}
                  l4Keys={l4Keys.length > 0 ? l4Keys : null}
                  l4Definitions={l4Definitions}
                  allowOr={false}
                  allowEmptyConditions
                  confirmRemove
                  fixedFirstRow={{
                    label: t('settings.proactive.hoursCondition'),
                    value: rule.minutes ?? DEFAULT_INTERVAL_MINUTES,
                    onChange: (v) => updateRule(idx, { minutes: v }),
                    unit: t('settings.proactive.minutesUnit') || 'min',
                    min: 1,
                    max: MAX_INTERVAL_MINUTES,
                    step: INTERVAL_STEP_MINUTES,
                  }}
                />
            </SettingsItemCard>
          ))}
          <button type="button" className="condition-add proactive-add-rule" onClick={addRule}>
            {t('settings.proactive.addRule')}
          </button>
        </div>
      </PageSection>
      <ConfirmModal
        open={deleteRuleIdx != null}
        title={t('settings.deleteRuleTitle')}
        message={t('settings.deleteRuleMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => deleteRuleIdx != null && removeRule(deleteRuleIdx)}
        onCancel={() => setDeleteRuleIdx(null)}
        danger
      />
    </SettingsFormWithPanel>
  )
}
